import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Strings } from "../../../../common/Common";
import {
  AuthenticationManager,
  ResourceID,
} from "../../../../common/AuthenticationManager";
import { TicketView } from "../../common/TicketView";
import TransactionInformation from "../../common/TransactionInformation";
import { RefundCreation } from "../../refunds/RefundCreation";
import "./../../../css/transaction-details.css";
import { BreadCrumb } from "../../../common/BreadCrumb";
import {
  NavigationItem,
  NavigationSettings,
} from "../../../common/NavigationSettings";
import { TransactionStatus } from "../../../../constants/TransactionConstants";
import { Transaction } from "../../../../models/Transaction";
import { useHistory } from "react-router";
import { PaymentCredit } from "../../../../models/PaymentCredit";
import { RefundRequest } from "../../../../models/RefundRequest";
import { HistoryDialog } from "../../../common/HistoryDialog";
import {
  ApplicationID,
  ObjectType,
  SystemSettingKeys,
} from "../../../../common/Constants";
import ButtonActionDetailGroup from "../../../common/ButtonActionDetailGroup";
import PaymentInformation from "./PaymentInformation";
import CustomerInformation from "./CustomerInformation";
import GeneralInformation from "./GeneralInformation";
import CardLayout from "../../../layout/CardLayout";
import { RefundStatus } from "../../../../constants/RefundConstants";
import { SystemSettings } from "../../../../common/SystemSettings";
import EVoucherReactivation from "./EVoucherReactivation";
import { TransactionService } from "../../../../services/TransactionService";
import { RefundService } from "../../../../services/RefundService";
import EmailResending from "./EmailResending";
import { PaymentEvoucher } from "../../../../models/PaymentEvoucher";
import { EVoucherService } from "../../../../services/EVoucherService";

const TPOS = ApplicationID.getName(ApplicationID.TPOS);
const PWSM = ApplicationID.getName(ApplicationID.PWSM);
const TKOS = ApplicationID.getName(ApplicationID.TKOS);

const getCreateRefundButtonTooltip = (
  transaction,
  refundRequests,
  orderDetail
) => {
  if (transaction.isRefund)
    return "Refund request cannot be created for refund transactions.";
  if (transaction.isCharged === undefined)
    return "Please view charged status before creating refund request";
  if (
    transaction.status !== TransactionStatus.Successful &&
    !transaction.isCharged
  )
    return "Refund request cannot be created as the transaction was not charged.";
  const hasVoucher = Object.keys(orderDetail.vouchers || {}).length > 0;
  if (hasVoucher)
    return "Refund request cannot be created for transactions with voucher(s) applied.";

  const hasPendingRequest =
    refundRequests.filter((x) => x.status === RefundStatus.Pending).length > 0;
  if (hasPendingRequest)
    return "A refund request created for this transaction is currently in pending status.";

  const refundedAmount = refundRequests
    .filter((x) => x.status !== RefundStatus.Rejected)
    .reduce(
      (refundedAmount, refundRequest) => refundedAmount + refundRequest.amount,
      0
    );
  if (refundedAmount >= transaction.amountFinal)
    return "The transaction has already been fully refunded.";

  return "";
};

const isRefundPeriodExpired = (transaction, refundRequests) => {
  if (
    transaction.salesChannel !== PWSM ||
    !refundRequests ||
    refundRequests.length === 0
  ) {
    return false;
  }

  const processedStatuses = [RefundStatus.Approved, RefundStatus.Successful];
  const isNotRefundable = refundRequests.some((x) =>
    processedStatuses.includes(x.status)
  );
  if (isNotRefundable) {
    return true;
  }

  const pendingRequest = refundRequests.find(
    (x) => x.status === RefundStatus.Pending
  );
  if (!pendingRequest) {
    return false;
  }

  const onlineRefundTimeoutInMinutes = SystemSettings.get(
    SystemSettingKeys.OnlineRefundTimeoutInMinutes
  );
  const expiredDateTime = new Date(pendingRequest.createdOn);
  expiredDateTime.setMinutes(
    expiredDateTime.getMinutes() + onlineRefundTimeoutInMinutes
  );
  return expiredDateTime <= new Date();
};

const getNavigationSettings = () => {
  return new NavigationSettings({
    parentModule: new NavigationItem({
      identifier: "transactions",
      name: "Transaction Management",
    }),
    activeModule: new NavigationItem({
      identifier: "transaction-history",
      name: "Transaction History",
    }),
  });
};

const TransactionDetails = ({ match: { params } }) => {
  const { id } = params;
  const [transaction, setTransaction] = useState(new Transaction({}));
  const [information, setInformation] = useState({});
  const [refundRequests, setRefundRequests] = useState([]);
  const [paymentCredit, setPaymentCredit] = useState(new PaymentCredit({}));
  const [paymentEVouchers, setPaymentEVouchers] = useState([]);
  const [eVouchers, setEVouchers] = useState([]);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [isLoadEVoucher, setIsLoadEVoucher] = useState(false);
  const [loadEVoucherCounter, setLoadEVoucherCounter] = useState(0);

  const history = useHistory();

  const service = useMemo(() => new TransactionService(), []);

  const loadTransactionInformation = useCallback(() => {
    service.getInformation(id).then((response) => {
      response.movieInfos.forEach((x) => {
        x.id = Strings.generateRandomString();
      });
      setInformation(response);
    });
  }, [id]);

  useEffect(() => {
    service
      .get(id, {
        includes:
          "Order,PaymentCredits,Prints,PaymentCashes,Prints.PrintItems,PaymentEVouchers",
      }) 
      .then(({ data }) => {
        const instance = new Transaction(data);
        setTransaction(instance);
        if (instance.paymentCredits && instance.paymentCredits.length > 0) {
          setPaymentCredit(new PaymentCredit(instance.paymentCredits[0]));
        }
        if (instance.paymentEVouchers && instance.paymentEVouchers.length > 0) {
          setPaymentEVouchers(
            instance.paymentEVouchers.map((x) => new PaymentEvoucher(x))
          );
        }
        loadTransactionInformation();
      });
  }, [id, loadTransactionInformation]);

  useEffect(() => {
    new RefundService()
      .list({ transactionId: id })
      .then(({ data: response }) => {
        if (response.data.length) {
          setRefundRequests(
            response.data.map((item) => new RefundRequest(item))
          );
        }
      });
  }, [id]);

  useEffect(() => {
    const fetchEVouchers = async () => {
      const eVoucherIds = paymentEVouchers
        .map((x) => x.eVoucherId)
        .filter((x) => x);
      if (!eVoucherIds.length) {
        return;
      }

      setIsLoadEVoucher(true);
      try {
        const { data: response } = await new EVoucherService().get(
          `?${eVoucherIds.map((x) => `ids=${x}`).join("&")}`
        );
        setEVouchers(response.data);
      } finally {
        setIsLoadEVoucher(false);
      }
    };
    fetchEVouchers();
  }, [paymentEVouchers, loadEVoucherCounter]);

  const onViewRefund = () => {
    history.push(`/transactions/${transaction.referenceNumber}/refunds`);
  };
  const onChargedStatusChanged = async (isCharged) => {
    if (isCharged) {
      setTransaction((prev) => ({
        ...prev,
        isCharged: isCharged,
      }));
    }
  };

  const grossAmount = (transaction.grossAmount ?? 0).toFixed(6);
  const disableViewRefund = refundRequests.length === 0 || transaction.isRefund;

  const createRefundButtonTooltip = getCreateRefundButtonTooltip(
    transaction,
    refundRequests,
    information
  );

  return (
    <div className="main-content">
      <BreadCrumb navigationSettings={getNavigationSettings()} />
      <div className="section__content section__content--p30">
        <div className="container-fluid">
          <ButtonActionDetailGroup>
            <EmailResending transaction={transaction} />
            {isLoadEVoucher ? (
              <ButtonActionDetailGroup.Loading className="btn btn-primary" />
            ) : (
              <EVoucherReactivation
                transaction={transaction}
                paymentEVouchers={paymentEVouchers}
                onSuccess={() => setLoadEVoucherCounter((x) => x + 1)}
                eVouchers={eVouchers}
              />
            )}
            <ButtonActionDetailGroup.Item
              className="btn btn-primary"
              id="btnRefund"
              onClick={onViewRefund}
              disabled={disableViewRefund}
            >
              View Refund
            </ButtonActionDetailGroup.Item>
            {AuthenticationManager.isAuthorized(
              ResourceID.CreateRefundRequestButton
            ) && (
              <ButtonActionDetailGroup.Item
                className="btn btn-primary float-right"
                data-toggle="modal"
                title={createRefundButtonTooltip}
                data-target="#RefundRequest"
                disabled={createRefundButtonTooltip}
              >
                <i className="fas fa-edit" /> Create Refund Request
              </ButtonActionDetailGroup.Item>
            )}
            <ButtonActionDetailGroup.Item
              className="btn btn-outline-secondary"
              onClick={() => setShowHistoryDialog(true)}
            >
              History
            </ButtonActionDetailGroup.Item>
          </ButtonActionDetailGroup>
          {isRefundPeriodExpired(transaction, refundRequests) && (
            <div
              className="alert alert-warning alert-dismissible fade show"
              role="alert"
            >
              This online request's period has expired.
            </div>
          )}
          <div className="row">
            <CardLayout
              id="general"
              className="col-md-6"
              bodyClassName="text-left"
              label="General Information"
            >
              <GeneralInformation transaction={transaction} />
            </CardLayout>
            {transaction.salesChannel !== TPOS &&
              transaction.salesChannel !== TKOS && (
                <CardLayout
                  id="cust"
                  className="col-md-6"
                  bodyClassName="text-left"
                  label="Customer Information"
                >
                  <CustomerInformation transaction={transaction} />
                </CardLayout>
              )}
          </div>
          <div className="row">
            <CardLayout
              id="payment"
              bodyClassName="text-left"
              label="Payment Information"
            >
              <PaymentInformation
                transaction={transaction}
                paymentCredit={paymentCredit}
                paymentEVouchers={paymentEVouchers}
                onChargedStatusChanged={onChargedStatusChanged}
                eVouchers={eVouchers}
              />
            </CardLayout>
          </div>
          <TransactionInformation information={information} />
        </div>
      </div>
      <TicketView transaction={transaction} information={information} />
      <RefundCreation
        transactionId={transaction.id}
        grossAmount={grossAmount}
      />
      {showHistoryDialog && (
        <HistoryDialog
          objectId={transaction.id}
          onClose={() => setShowHistoryDialog(false)}
          url="/api/v1/history_logs"
          objectType={ObjectType.Transaction}
        />
      )}
    </div>
  );
};

export default TransactionDetails;
